import type {
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  RaRecord,
} from "react-admin";
import { DateTime } from "luxon";
import { applyPagination, applySort } from "../backoffice.utils";
import { DrivingLicenseClass } from "../model/DrivingLicenseClass";
import { Student, studentsProvider } from "./studentsProvider";
import { AbstractDataProvider } from "./AbstractDataProvider";

export interface BookedQuote extends RaRecord {
  id: string;
  createdAt?: DateTime;
  cancelledAt?: DateTime;
  bundleName: string;
  drivingLicenseClasses: Array<DrivingLicenseClass>;
  drivingSchoolId: string;
  instructorId?: string | null;
  studentId: string;
  trainingIds: Array<string>;
  bookedTrainingIds: Array<string>;
  prices: { [productName: string]: number };
  isFinished: boolean;
  isCancelled: boolean;
  statusSortOrder: number;
}

class BookedQuotesProvider extends AbstractDataProvider<BookedQuote> {
  async getList(_: string, { filter, sort, pagination }: GetListParams): Promise<GetListResult<BookedQuote>> {
    const { studentId } = filter;
    if (!studentId || Object.keys(filter).length !== 1) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"studentId": ...}`);
    }
    const { data: student } = await studentsProvider.getOne("students", { id: studentId });
    const bookedQuotes = this.getBookedQuotes(student);
    return applyPagination(applySort(bookedQuotes, sort), pagination);
  }

  async getManyReference(
    resource: string,
    { id, target, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<BookedQuote>> {
    if (filter && Object.keys(filter).length > 0) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {}`);
    }
    if (target !== "studentId") {
      throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "studentId"`);
    }
    return this.getList(resource, { filter: { studentId: id }, sort, pagination });
  }

  getBookedQuotes(student: Student): Array<BookedQuote> {
    const bookedQuotes: Array<BookedQuote> = [];
    for (const bookedTraining of student.bookedTrainings) {
      const { quoteId } = bookedTraining;
      const bookedQuote = bookedQuotes.find((it) => it.id === quoteId);
      if (bookedQuote) {
        if (bookedTraining.isForDrivingLicense) {
          bookedQuote.drivingLicenseClasses.push(bookedTraining.drivingLicenseClass);
          bookedQuote.drivingLicenseClasses.sort();
        }
        bookedQuote.trainingIds.push(bookedTraining.trainingId);
        bookedQuote.bookedTrainingIds.push(bookedTraining.id);
        bookedQuote.isFinished = bookedQuote.isFinished && !!bookedTraining.isFinished;
        bookedQuote.isCancelled = bookedQuote.isCancelled && !!bookedTraining.isCancelled;
        Object.assign(bookedQuote.prices, bookedTraining.prices);
        if (bookedTraining.isCancelled && bookedTraining.cancelledAt) {
          if (!bookedQuote.cancelledAt || bookedTraining.cancelledAt > bookedQuote.cancelledAt) {
            bookedQuote.cancelledAt = bookedTraining.cancelledAt;
          }
        }
      } else {
        bookedQuotes.push({
          id: quoteId,
          bundleName: bookedTraining.bundleName,
          createdAt: bookedTraining.createdAt,
          cancelledAt: bookedTraining.isCancelled ? (bookedTraining.cancelledAt ?? undefined) : undefined,
          drivingLicenseClasses: bookedTraining.isForDrivingLicense ? [bookedTraining.drivingLicenseClass] : [],
          prices: { ...bookedTraining.prices },
          drivingSchoolId: bookedTraining.drivingSchoolId,
          instructorId: bookedTraining.instructorId,
          trainingIds: [bookedTraining.trainingId],
          bookedTrainingIds: [bookedTraining.id],
          studentId: student.id,
          isFinished: !!bookedTraining.isFinished,
          isCancelled: !!bookedTraining.isCancelled,
          statusSortOrder: 0,
        });
      }
    }
    for (const bookedQuote of bookedQuotes) {
      if (!bookedQuote.isCancelled) {
        bookedQuote.cancelledAt = undefined;
      }
      bookedQuote.statusSortOrder = bookedQuote.isCancelled ? 2 : bookedQuote.isFinished ? 1 : 0;
    }
    return bookedQuotes;
  }
}

export const bookedQuotesProvides = new BookedQuotesProvider();
